// THE SEASON, as the client reads it. The server owns every derivation
// (`state`, `label`, `current_run`); this file only spells the labels the UI
// needs for a year that may not exist on the server yet (« Ouvrir 2027 »),
// and the row's verb table.
import type { components } from '@/api/schema'

export type SeasonRunState = 'idle' | 'open' | 'closed'

// « 2026 », or « 2026-2027 » for a season that straddles two calendar years —
// the same rule WorkPeriod#label_for spells server-side.
export const seasonLabel = (year: number, wrapsYear: boolean): string =>
  wrapsYear ? `${year}-${year + 1}` : String(year)

// The year « Ouvrir » proposes on a season row: the idle run's, else the year
// after the latest run, else the season's SERVED floor (`earliest_openable_year`:
// this year, or LAST year for a season that wraps — « Hiver 2025-2026 » is the
// live winter in February 2026, and a shop that starts mid-winter means that
// one; his call, 2026-10-25: the earlier winter is proposed, the dialog shows
// both). Never below the floor, so the button never offers a year the server
// refuses.
export const nextYearToOpen = (
  run: { state?: SeasonRunState; season_year?: number } | null | undefined,
  floor: number,
): number => {
  if (!run || run.season_year == null) return floor
  if (run.state === 'idle') return run.season_year >= floor ? run.season_year : floor
  return Math.max(run.season_year + 1, floor)
}

// ONE SEASON MENU, THREE DOORS: the intake line's « changer », the job header's
// season fact and « Renouveler ▾ » all list the same short menu — the type's
// seasons × three years from each season's START YEAR, in the shop's order,
// the default checked. No run has to exist for a choice to be offered: the run
// is born on demand when the job names it.
export type SeasonChoice = { workPeriodId: string; seasonYear: number; label: string; key: string }
// `currentRun` = the season's row run as the server picks it (open → idle with
// jobs → latest closed); `earliestOpenableYear` = the season's served floor
// (this year, or last year for a wrapping season). Both ride the catalog.
export type SeasonSpec = {
  id: string; name: string; wrapsYear: boolean
  earliestOpenableYear: number
  currentRun?: { year: number; state: SeasonRunState } | null
}

// THE START YEAR of a season's menu — one rule for the three doors, so the
// intake line and « Renouveler » cannot start a season on different years:
// the OPEN run's year (or an idle one's — next year's jobs already wait
// there), else the year after the latest CLOSED one, else the served floor.
// Never below the floor. ⚠ The floor, not the calendar year: a wrapping
// season's live winter is keyed on LAST year from January to spring, and a
// calendar floor hid it from every menu (the served default was not even in
// its own list).
export const startYear = (season: SeasonSpec): number => {
  const floor = season.earliestOpenableYear
  const run = season.currentRun
  if (!run) return floor
  if (run.state === 'closed') return Math.max(run.year + 1, floor)
  return Math.max(run.year, floor)
}

// Years then the shop order: every season's year N before any season's N+1.
// The order WITHIN a year is the feed's — the server lists seasons in the
// shop's order (the calendar as history knows it) and a stable sort keeps it.
const sortChoices = (choices: SeasonChoice[]): SeasonChoice[] =>
  choices.sort((a, b) => a.seasonYear - b.seasonYear)

const choice = (season: SeasonSpec, year: number): SeasonChoice => ({
  workPeriodId: season.id,
  seasonYear: year,
  // Always the name — the same spelling as the served `season_label`, so a
  // choice can be matched to a job's fact and a lone season still reads
  // « Taille 2027 » rather than a bare year.
  label: seasonChoiceLabel(season, year),
  key: `${season.id}:${year}`,
})

// Three years per season from a start the caller derives per season (the
// intake line and the header fact: `startYear`; « Renouveler »: the year after
// the parent's, floored there).
const choicesFrom = (seasons: SeasonSpec[], from: (season: SeasonSpec) => number, years: number): SeasonChoice[] =>
  sortChoices(seasons.flatMap(season => Array.from({ length: years }, (_, i) => choice(season, from(season) + i))))

export const seasonChoices = (seasons: SeasonSpec[], years = 3): SeasonChoice[] =>
  choicesFrom(seasons, startYear, years)

// « Taille 2027 » — the season's name with its year, the label a job carries.
export const seasonChoiceLabel = (season: SeasonSpec, year: number): string =>
  `${season.name} ${seasonLabel(year, season.wrapsYear)}`

// « RENOUVELER POUR … ▾ »'s menu: the years AFTER the renewed job's own season
// (a renewal is next year's sale, never this year's again), and never behind
// the season's start — renewing a 2026 job in January 2027 proposes 2027
// first. No run has to exist: the run is born on demand when the child names it.
export const renewChoices = (seasons: SeasonSpec[], parentSeasonYear: number | null): SeasonChoice[] =>
  choicesFrom(seasons, season => Math.max((parentSeasonYear ?? startYear(season) - 1) + 1, startYear(season)), 3)

// A run's FACE — the one state word the index row and the season page's year
// rows show, for the run the server picked. Five words, five faces; the alarm
// is a word too (« Composeur arrêté »), never a sentence or a colour bar.
export type SeasonRowFace =
  | 'open'            // Ouverte · ceiling ahead or none → Composer jusqu'au · Fermer
  | 'ceiling_passed'  // Composeur arrêté (red) → Composer jusqu'au · Fermer (peach)
  | 'idle'            // En attente → Ouvrir (peach)
  | 'closed'          // Fermée → Rouvrir, only on the last year that was opened
  | 'none'            // Aucune (no run yet)

export const seasonRowFace = (run: { state?: SeasonRunState; compose_until?: string | null } | null | undefined, today: string): SeasonRowFace => {
  if (!run || !run.state) return 'none'
  if (run.state === 'open') return run.compose_until && run.compose_until < today ? 'ceiling_passed' : 'open'
  return run.state
}

// The chip tone per face — the app's one palette: teal = a live state, red =
// the alarm, blue = dated/waiting, grey = nothing live.
export const SEASON_FACE_TONE: Record<SeasonRowFace, 'bank' | 'late' | 'dated' | 'none'> = {
  open: 'bank', ceiling_passed: 'late', idle: 'dated', closed: 'none', none: 'none',
}

// THE RENEWAL CAMPAIGN of a year — its own window, apart from the year's
// state: « Pas commencée » until « Lancer » (or « Ouvrir » on the season's next
// campaign year), « En cours », « Terminée » (« Terminer » or « Fermer »).
// A reading of the SERVED `renewal_campaign_state`; a year with no run yet
// has no campaign either.
export type CampaignState = 'not_started' | 'live' | 'ended'
export const campaignStatus = (run: { renewal_campaign_state?: CampaignState } | null | undefined): CampaignState =>
  run?.renewal_campaign_state ?? 'not_started'
export const CAMPAIGN_TONE: Record<CampaignState, 'bank' | 'dated' | 'none'> = {
  live: 'bank', not_started: 'dated', ended: 'none',
}

// « ÉVALUATION EN ATTENTE DE L'OUVERTURE DE LA SAISON » — both job pages:
// the job's OWN season has not opened (served `evaluations_held` — never true
// on a closed season) and an
// evaluation still waits for a date. The one place a held evaluation explains
// itself: it left the composer and every pool.
export const evaluationWaits = (
  job: { attributes: { evaluations_held?: boolean } },
  evaluations: readonly { attributes: { status: string } }[],
): boolean => job.attributes.evaluations_held === true && evaluations.some(v => v.attributes.status === 'pending')

// A run RESOURCE in the `current_run` shape the three run dialogs take — the
// board's chip and the season page both hand them a run from the runs feed.
export type SeasonRunLike = {
  id: string
  attributes: {
    label: string; season_year: number; state: SeasonRunState
    opened_on?: string | null; compose_from?: string | null; compose_until?: string | null; closed_on?: string | null
    jobs_count?: number | null
  }
}
export const toCurrentRun = (resource: SeasonRunLike) => {
  const a = resource.attributes
  return {
    id: resource.id, label: a.label, season_year: a.season_year, state: a.state,
    opened_on: a.opened_on ?? null, compose_from: a.compose_from ?? null, compose_until: a.compose_until ?? null,
    closed_on: a.closed_on ?? null,
    jobs_count: a.jobs_count ?? 0,
  }
}

// ── A JOB TYPE'S RANGES ON A YEAR ───────────────────────────────────────────
// Per type, per season YEAR, two optional ranges — « Travaux du … au … » and
// « Évaluations du … au … » — each NARROWING the year, blank = the whole
// season. The server serves the entry (WorkPeriodRun::TypeWindowsView): the
// type's OWN dates and the EFFECTIVE ones the composer really uses. This file
// only picks the words; it never intersects two ranges (derived state is served).
export type TypeWindowEntry = components['schemas']['TypeWindowEntry']
export type TypeWindowSide = 'work' | 'evaluation'

// The words of one range: an i18n key under `workPeriods.typeWindows.line` and
// its DATES (ISO — the caller formats them). `muted` = the line reads quiet
// (nothing of the type's own, or a range already behind us).
export type TypeWindowLineKey =
  | 'wholeSeason' | 'wholeSeasonAtOpening' | 'wholeSeasonBetween' | 'wholeSeasonFrom' | 'wholeSeasonUntil'
  | 'between' | 'from' | 'until' | 'starts' | 'ended' | 'empty'
export type TypeWindowLine = { key: TypeWindowLineKey; dates: { from?: string; until?: string }; muted: boolean }

type RunForLine = { state?: SeasonRunState } | null | undefined

const ownSide = (entry: TypeWindowEntry, side: TypeWindowSide) =>
  side === 'work'
    ? { from: entry.compose_from, until: entry.compose_until }
    : { from: entry.evaluation_from, until: entry.evaluation_until }

const effectiveSide = (entry: TypeWindowEntry, side: TypeWindowSide) =>
  side === 'work'
    ? { from: entry.effective_work_from, until: entry.effective_work_until }
    : { from: entry.effective_evaluation_from, until: entry.effective_evaluation_until }

// Whether the type has a row of its own this year (any of the four dates).
export const typeWindowSet = (entry: TypeWindowEntry | null | undefined): boolean =>
  Boolean(entry && (entry.compose_from || entry.compose_until || entry.evaluation_from || entry.evaluation_until))

const rangeLine = (from: string | null, until: string | null, muted = false): TypeWindowLine => {
  if (from && until) return { key: 'between', dates: { from, until }, muted }
  if (from) return { key: 'from', dates: { from }, muted }
  if (until) return { key: 'until', dates: { until }, muted }
  return { key: 'wholeSeason', dates: {}, muted: true }
}

const wholeSeasonLine = (from: string | null, until: string | null): TypeWindowLine => {
  if (from && until) return { key: 'wholeSeasonBetween', dates: { from, until }, muted: true }
  if (from) return { key: 'wholeSeasonFrom', dates: { from }, muted: true }
  if (until) return { key: 'wholeSeasonUntil', dates: { until }, muted: true }
  return { key: 'wholeSeason', dates: {}, muted: true }
}

// THE TYPE LINE, one range — the plan's state table:
//   no run yet        « Toute la saison »
//   idle              unset « Toute la saison · dates à l'ouverture »; set = its own dates
//   open, before du   unset « Toute la saison · … »; set « Commence le 1 mai »
//   open, inside      unset the season's dates (muted); set the EFFECTIVE range
//                     (the season fills a blank side)
//   open, au passed   « Terminé le 15 août » (muted)
//   closed            read-only: its own dates, else « Toute la saison »
//   empty effective   « fenêtre vide · rien ne sera proposé »
export const typeWindowLine = (
  entry: TypeWindowEntry | null | undefined,
  run: RunForLine,
  side: TypeWindowSide,
  today: string,
): TypeWindowLine => {
  if (!run?.state || !entry) return { key: 'wholeSeason', dates: {}, muted: true }
  const own = ownSide(entry, side)
  const effective = effectiveSide(entry, side)
  const set = Boolean(own.from || own.until)

  if (run.state === 'closed') return rangeLine(own.from, own.until, !set)

  if (set && effective.from && effective.until && effective.from > effective.until) {
    return { key: 'empty', dates: {}, muted: false }
  }

  if (run.state === 'idle') {
    if (set) return rangeLine(own.from, own.until)
    return effective.from || effective.until
      ? wholeSeasonLine(effective.from, effective.until)
      : { key: 'wholeSeasonAtOpening', dates: {}, muted: true }
  }

  // open
  if (!set) return wholeSeasonLine(effective.from, effective.until)
  if (effective.until && effective.until < today) return { key: 'ended', dates: { until: effective.until }, muted: true }
  if (effective.from && effective.from > today) return { key: 'starts', dates: { from: effective.from }, muted: false }
  return rangeLine(effective.from, effective.until)
}

// The four dates the type's popover opens on: its own row, else last year's
// moved to this year — a VISIBLE suggestion (`suggested`), never saved until
// « Enregistrer ». A year with no run yet has no served entry: the caller hands
// in last year's entry and its OWN dates move one year here (the same day one
// year on, 29 février → 28 — as the server shifts its prefill).
export type TypeWindowDraft = { composeFrom: string; composeUntil: string; evaluationFrom: string; evaluationUntil: string }

export const shiftYear = (date: string | null | undefined, years = 1): string => {
  if (!date) return ''
  const [ y, m, d ] = date.split('-').map(Number)
  const year = y + years
  const lastDay = new Date(Date.UTC(year, m, 0)).getUTCDate()
  return `${year}-${String(m).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`
}

export const typeWindowDraft = (
  entry: TypeWindowEntry | null | undefined,
  lastYear?: TypeWindowEntry | null,
): { draft: TypeWindowDraft; suggested: boolean } => {
  if (entry && typeWindowSet(entry)) {
    return {
      draft: {
        composeFrom: entry.compose_from ?? '', composeUntil: entry.compose_until ?? '',
        evaluationFrom: entry.evaluation_from ?? '', evaluationUntil: entry.evaluation_until ?? '',
      },
      suggested: false,
    }
  }
  const draft: TypeWindowDraft = entry
    ? {
        composeFrom: entry.prefill_compose_from ?? '', composeUntil: entry.prefill_compose_until ?? '',
        evaluationFrom: entry.prefill_evaluation_from ?? '', evaluationUntil: entry.prefill_evaluation_until ?? '',
      }
    : {
        composeFrom: shiftYear(lastYear?.compose_from), composeUntil: shiftYear(lastYear?.compose_until),
        evaluationFrom: shiftYear(lastYear?.evaluation_from), evaluationUntil: shiftYear(lastYear?.evaluation_until),
      }
  return { draft, suggested: Object.values(draft).some(Boolean) }
}

// The year a type's row reads on its page: the OPEN year, else the next one to
// open (the lowest idle year), else the latest closed one (read-only). Null =
// the season has no year yet.
export const currentTypeRun = <R extends { attributes: { state: SeasonRunState; season_year: number } }>(runs: R[]): R | null => {
  const open = runs.find(r => r.attributes.state === 'open')
  if (open) return open
  const idle = runs.filter(r => r.attributes.state === 'idle').sort((a, b) => a.attributes.season_year - b.attributes.season_year)[0]
  if (idle) return idle
  return runs.filter(r => r.attributes.state === 'closed').sort((a, b) => b.attributes.season_year - a.attributes.season_year)[0] ?? null
}
